import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useCheckUnderwriting, usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage } from "src/components";
import DropdownSelector from "src/components/DropdownSelector";

import { RejectedReason, enums, formatters } from "@fraction/shared";

export interface TimeSinceBankruptcyProps {
  onChange: (value: number) => void;
  value?: number;
  title?: string;
  applicantId?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const TimeSinceBankruptcy = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  title,
  applicantId,
  ...props
}: TimeSinceBankruptcyProps) => {
  const validate = useCallback(() => !!value, [value]);

  const handleChange = useCallback(
    (val: string) => {
      onChange?.(formatters.number.toNumber(val));
    },
    [onChange]
  );

  const runUnderwriting = useCheckUnderwriting({
    failMessage:
      "Unfortunately, Fraction requires that you have not declared bankruptcy within the last 7 years.",
    getSpecificUnderwritingResult: (result) => {
      if (!applicantId) {
        return;
      }
      return result?.applicantProperties?.[applicantId]?.[
        enums.UnderwritingRuleName.SELF_DECLARED_BANKRUPTCY_CHECK
      ];
    },
    onNext,
    onChange: handleChange,
  });

  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({
    onNext: runUnderwriting.mutateAsync,
    validate,
    reasonsToBlock: [RejectedReason.BANKRUPTCY],
  });

  return (
    <FormFlowPage
      {...props}
      title={title || "How long ago did you declare bankruptcy?"}
      description="If you are the primary applicant, Fraction requires that you haven't declared bankruptcy in the past 7 years."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={runUnderwriting.nextDisabled || nextDisabled}
      nextLoading={runUnderwriting.isPending || nextLoading}
    >
      <DropdownSelector
        focusRef={focusRef}
        value={value?.toString()}
        onChange={runUnderwriting.onChange}
        label="Time since bankruptcy"
        options={[
          { value: "1", label: "Less than 1 year ago" },
          { value: "3", label: "Less than 3 years ago" },
          { value: "7", label: "Less than 6 years ago" },
          { value: "8", label: "7 or more years ago" },
        ]}
      />
    </FormFlowPage>
  );
};

export default memo(TimeSinceBankruptcy);
